import { entity, legalPaths } from '../entity';
import {
  P, H3, Ul, Callout, Tok,
} from '../blocks';
import type { LegalDoc } from '../types';

// Normas de la Comunidad (español).
//
// Traducción de guidelines.en.tsx — mismos ids de sección, misma numeración.
// Si editas uno, edita el otro.
//
// La sección "enforcement" describe lo que el código hace realmente y debe
// seguir siendo fiel a él: rechazo por toxicidad >= 0.7, degradación en búsqueda
// >= 0.5 (contentSafe = false, penalización de -500), revisión de avatares por
// Rekognition, y los mensajes privados NO se analizan. Ver el comentario en
// guidelines.en.tsx para las referencias exactas al código.
//
// Nunca afirmar detección proactiva de CSAM por huella digital: checkKnownCsam
// es un stub. El compromiso de reporte es sobre lo que hacemos al tener
// conocimiento efectivo, que es una obligación legal (18 U.S.C. 2258A).

export const guidelinesEs: LegalDoc = {
  lang: 'es',
  title: 'Normas de la Comunidad',
  updatedLabel: `Última actualización: ${entity.effectiveDate}`,
  contentsLabel: 'Contenido',
  altHref: legalPaths.guidelines,
  altLabel: 'View in English',
  sections: [
    {
      id: 'what-this-is',
      heading: '1. Para qué es todo esto',
      body: (
        <>
          <P>
            Tribys existe para que personas con intereses afines se encuentren y
            de verdad se reúnan. Eso solo funciona si la aplicación se siente lo
            bastante segura para entrar a un cuarto lleno de desconocidos. Estas
            Normas son la forma en que lo mantenemos así.
          </P>
          <Callout>
            <strong>
              La versión corta: sé una persona real, trata bien a la gente y
              recuerda que cualquiera aquí podría terminar en el mismo cuarto que
              tú.
            </strong>
          </Callout>
          <P>
            Estas Normas forman parte de nuestros{' '}
            <a href={legalPaths.termsEs}>Términos del Servicio</a>, así que
            violarlas incumple ese acuerdo. Aplican a todo lo que aportas a
            Tribys: tu perfil, tus fotos, tus mensajes, los tribys que creas, los
            Spots que agregas y cómo te comportas cuando conoces en persona a
            alguien que encontraste aquí.
          </P>
          <P>
            Actualizamos estas Normas conforme aparecen problemas nuevos. No son
            una lista exhaustiva de todo lo que alguien podría hacer mal; si algo
            va evidentemente en contra de su espíritu, actuaremos igual.
          </P>
        </>
      ),
    },
    {
      id: 'real-person',
      heading: '2. Sé tú, y solo tú',
      body: (
        <>
          <P>
            Todo aquí depende de que las personas sean quienes dicen ser.
          </P>
          <Ul
            items={[
              'Usa tu nombre real y una foto reciente de tu propio rostro.',
              'Una cuenta por persona. Sin segundas cuentas, sin cuentas para tu amistad, sin cuentas para evadir un bloqueo o una suspensión.',
              'Debes tener 18 años o más. Sin excepciones.',
              'No finjas ser alguien más: otro miembro, una celebridad, un negocio, un organizador, un funcionario público o nosotros.',
              'No uses un perfil para representar un negocio, una marca, una banda, una campaña o una organización. Los perfiles son para personas.',
              'No pongas datos de contacto, enlaces, precios ni textos promocionales en tu perfil para sacar a la gente de Tribys.',
            ]}
          />
          <P>
            La verificación de identidad es opcional y está bien omitirla. Pero si
            te verificas y luego subes la foto de otra persona, lo vamos a notar, y
            lo tratamos como una violación grave: el sentido de la insignia es
            justamente que no se puede prestar.
          </P>
        </>
      ),
    },
    {
      id: 'photos',
      heading: '3. Fotos',
      body: (
        <>
          <P>
            Tu foto de perfil debe mostrar tu rostro con claridad y ser reciente.
          </P>
          <P>No subas:</P>
          <Ul
            items={[
              'fotos de personas que no aceptaron aparecer en tu perfil;',
              'fotos de niñas o niños, incluidos los tuyos, como foto de perfil;',
              'desnudos ni contenido sexual — consulta la sección 5;',
              'fotos que sean principalmente texto, memes, logotipos, códigos QR o anuncios;',
              'fotos que no tomaste y sobre las que no tienes derechos;',
              'violencia gráfica, lesiones o imágenes de la muerte de una persona real;',
              'fotos cuyo tema principal sean armas.',
            ]}
          />
          <P>
            Las fotos en grupo están bien si queda claro cuál eres tú y todas las
            personas de la foto están cómodas con aparecer.
          </P>
        </>
      ),
    },
    {
      id: 'respect',
      heading: '4. Acoso y odio',
      body: (
        <>
          <P>
            Estar en desacuerdo con alguien está bien. Volverle Tribys
            desagradable, no.
          </P>
          <P><strong>No se permite:</strong></P>
          <Ul
            items={[
              'insultos, epítetos, apodos ofensivos, burlas o contacto insistente no deseado;',
              'escribirle a alguien de nuevo después de que dejó claro que no le interesa, o después de que te bloqueó y encontraste otra forma de contactarle;',
              'comentarios, propuestas o insinuaciones sexuales que la otra persona no invitó;',
              'coordinarte con otras personas para atacar a alguien, dentro o fuera de la aplicación;',
              'atacar, degradar o deshumanizar a personas por su raza, etnia, origen nacional, situación migratoria, religión, discapacidad, condición médica, edad, sexo, identidad de género, expresión de género, orientación sexual o cualquier característica similar;',
              'símbolos de odio, o apoyo a organizaciones que promueven el odio o la violencia;',
              'referirse deliberadamente a alguien con un género que no le corresponde, o usar de forma reiterada un nombre que pidió no usar;',
              'excluir a personas de un triby con base en cualquiera de las características anteriores. La única excepción es el ajuste de composición de género integrado al crear un triby: un club de lectura solo de mujeres o un grupo de salud mental para hombres está bien. Lo que no está bien es usar el título o la descripción de un triby para dejar fuera, degradar o atacar a un grupo de personas.',
            ]}
          />
          <P>
            Si una conversación va mal, puedes salirte. Bloquear a alguien impide
            que te contacte o te vea, y nunca tienes que explicar por qué.
          </P>
        </>
      ),
    },
    {
      id: 'sexual',
      heading: '5. Contenido sexual y desnudos',
      body: (
        <>
          <P>
            <strong>
              Tribys no es una aplicación de citas ni una aplicación para adultos.
            </strong>{' '}
            Mantenlo en lo que te resultaría cómodo en un primer encuentro en una
            cafetería.
          </P>
          <P><strong>No se permite en ningún lugar:</strong></P>
          <Ul
            items={[
              'desnudos, desnudos parciales o imágenes sexualmente explícitas, ni en perfiles, ni en tribys, ni en Spots, ni en mensajes;',
              'descripciones o peticiones sexuales enviadas a alguien que no las invitó;',
              'servicios sexuales, acompañamiento, ni nada transaccional que involucre sexo;',
              'tribys o Spots organizados alrededor de actividad sexual;',
              'enlaces a contenido o sitios para adultos.',
            ]}
          />
          <P>
            Enviar una imagen sexual no solicitada es motivo de expulsión inmediata
            de Tribys, no de una advertencia.
          </P>
        </>
      ),
    },
    {
      id: 'minors',
      heading: '6. Menores de edad — cero tolerancia',
      body: (
        <>
          <Callout>
            <strong>
              Aquí no hay nada que discutir. Tribys es para mayores de 18 años.
              Cualquier contenido sexual que involucre a un menor, y cualquier
              intento de contactar, acosar sexualmente o quedar de ver a un menor,
              resulta en expulsión permanente inmediata y en un reporte a las
              autoridades.
            </strong>
          </Callout>
          <P>
            Eliminamos cualquier cuenta que encontremos perteneciente a un menor de
            18 años. Si tenemos conocimiento de contenido que aparente explotación
            sexual infantil, lo retiramos, cancelamos la cuenta de forma permanente,
            conservamos lo que la ley nos obliga a conservar y lo reportamos al
            National Center for Missing &amp; Exploited Children, como lo exige la
            legislación de Estados Unidos.
          </P>
          <P>
            Si te encuentras con algo de esto, repórtalo de inmediato en la
            aplicación y, si puedes, escribe a{' '}
            <Tok value={entity.supportEmail} /> para que nos llegue más rápido. Si
            un menor está en peligro inmediato, contacta primero a los servicios de
            emergencia de tu localidad.
          </P>
        </>
      ),
    },
    {
      id: 'violence',
      heading: '7. Violencia, amenazas y autolesiones',
      body: (
        <>
          <P><strong>No se permite:</strong></P>
          <Ul
            items={[
              'amenazar a alguien con violencia, o desearle violencia;',
              'fomentar u organizar violencia, o elogiar un ataque violento o a quien lo cometió;',
              'apoyo o promoción del terrorismo o del extremismo violento;',
              'animar a alguien a lastimarse, o promover el suicidio, las autolesiones o los trastornos alimenticios;',
              'violencia gráfica o contenido morboso;',
              'trata de personas o su explotación.',
            ]}
          />
          <P>
            <strong>Si lo estás pasando mal</strong>, hablar de ello no incumple
            nada. Hablar de tu propia salud mental, o apoyar a alguien más, es
            bienvenido aquí. Lo que retiramos es el contenido que fomenta el daño.
            Si alguien está en peligro inmediato, contacta a los servicios de
            emergencia o de crisis de tu localidad: no somos un servicio de crisis
            y no podemos responder con la rapidez necesaria para serlo.
          </P>
        </>
      ),
    },
    {
      id: 'illegal',
      heading: '8. Cosas ilícitas y reguladas',
      body: (
        <>
          <P>
            No uses Tribys para hacer, organizar, promover o anunciar nada ilícito.
            Eso incluye:
          </P>
          <Ul
            items={[
              'vender o gestionar drogas, medicamentos controlados, armas, municiones, explosivos o bienes robados;',
              'apuestas, loterías o juegos de azar;',
              'productos falsificados o sin licencia;',
              'fraude, robo de identidad, o comercio de cuentas o de datos personales;',
              'organizar una reunión que requiera un permiso que no tienes, o que sea ilícita donde se realiza.',
            ]}
          />
          <P>
            Las actividades legales para adultos están bien. Un triby que se reúne
            en un bar, una cervecería, una cata de vinos o un salón de puros es
            bienvenido; un triby sobre cómo conseguir drogas, no.
          </P>
        </>
      ),
    },
    {
      id: 'spam',
      heading: '9. Spam, fraudes y ventas',
      body: (
        <>
          <P>
            Tribys no es un canal de marketing. Tampoco es una lista de prospectos.
          </P>
          <P><strong>No se permite:</strong></P>
          <Ul
            items={[
              'enviar el mismo mensaje a muchas personas, o escribirle a gente con la que no tienes intención de reunirte;',
              'promover un negocio, producto, servicio, curso, asesoría, inversión o recaudación de fondos;',
              'mercadeo multinivel, granjas de referidos, esquemas de cripto o de inversión, o cualquier cosa con una red de reclutamiento;',
              'pedir dinero, regalos, tarjetas o cripto a los miembros, por cualquier motivo;',
              'phishing, enlaces falsos, o hacerse pasar por el soporte de Tribys;',
              'usar bots, scripts o automatización para crear cuentas, unirte a tribys o enviar mensajes;',
              'recopilar de forma masiva perfiles, fotos, datos de contacto o ubicaciones de los miembros, con cualquier propósito;',
              'crear tribys o Spots que existan para llevar tráfico a otro lugar.',
            ]}
          />
          <Callout>
            <strong>Nadie legítimo te va a pedir dinero en Tribys.</strong> Si
            alguien te saca rápido de la aplicación, te cuenta una historia que
            necesita fondos urgentes, o te ofrece una inversión, es un fraude. No
            envíes nada y repórtale: eso es lo que de verdad logra que se le
            expulse.
          </Callout>
          <P>
            Organizar una reunión sobre un pasatiempo del que además vendes cosas
            es un área gris. Usa el criterio: si el triby es de verdad sobre el
            interés, está bien. Si es de verdad sobre la venta, no.
          </P>
        </>
      ),
    },
    {
      id: 'privacy',
      heading: '10. La privacidad de los demás',
      body: (
        <>
          <P>
            Las personas comparten cosas aquí entendiendo que se quedan en el
            cuarto.
          </P>
          <Ul
            items={[
              'No publiques el domicilio, teléfono, correo, lugar de trabajo, datos financieros o documentos de nadie.',
              'No compartas capturas de conversaciones, perfiles o listas de miembros fuera de Tribys.',
              'No fotografíes ni grabes a las personas en una reunión sin pedirles permiso.',
              'No expongas a nadie: su sexualidad, identidad de género, salud, situación migratoria, religión, ni nada que no haya elegido compartir.',
              'No intentes deducir ni compartir dónde vive alguien a partir de lo que publicó.',
              'No pases la información de alguien a un tercero, ni uses lo que aprendes aquí para contactar, rastrear o presionar a alguien en otro lugar.',
            ]}
          />
          <P>
            Reportarnos a alguien no es una violación de privacidad: es como esto
            funciona, y tratamos los reportes de forma confidencial.
          </P>
        </>
      ),
    },
    {
      id: 'tribys-spots',
      heading: '11. Tribys y Spots',
      body: (
        <>
          <P>
            Un triby es un grupo construido alrededor de un interés. Un Spot es un
            lugar o un evento en el mapa. Ambos los crean los miembros, y ambos los
            ven personas que están decidiendo si aparecerse en algún lado, así que
            la exactitud importa más de lo que parece.
          </P>
          <H3>Tribys</H3>
          <Ul
            items={[
              'Describe con honestidad qué es el grupo y qué pasa en él.',
              'No crees tribys diseñados para excluir personas por alguna de las características de la sección 4. Elegir una composición de género con el ajuste integrado está bien; escribir exclusiones en el título o la descripción, no.',
              'No uses un triby como anuncio, lista de correos o embudo de ventas.',
              'Si organizas una reunión, preséntate, o avisa que no podrás.',
            ]}
          />
          <H3>Spots</H3>
          <Ul
            items={[
              'Agrega lugares y eventos reales, con el nombre, la dirección y los horarios correctos.',
              'No inventes un Spot, no agregues Spots de broma, y no pongas un Spot en un domicilio particular.',
              <><strong>Sube únicamente fotos que tomaste tú.</strong> Las fotos del sitio web de un negocio, de redes sociales, de Google o de un sitio de reseñas pertenecen a quien las tomó. Los logotipos, carteles, volantes y menús pertenecen a quien los diseñó. La sección 8 de los <a href={legalPaths.termsEs}>Términos</a> cubre esto, y tú eres responsable.</>,
              'No presentes un Spot como oficial, patrocinado o respaldado por un negocio u organizador salvo que genuinamente lo sea.',
              'No importes Spots de forma masiva desde otro mapa o directorio.',
            ]}
          />
          <P>
            <strong>
              Si un Spot es sobre tu negocio, tu establecimiento o tu evento
            </strong>{' '}
            y quieres que se corrija o se retire, repórtalo con el motivo{' '}
            <strong>propiedad intelectual</strong> o escribe a{' '}
            <Tok value={entity.copyrightEmail} />. La sección 9 de los{' '}
            <a href={legalPaths.termsEs}>Términos</a> establece el procedimiento
            formal de derechos de autor.
          </P>
        </>
      ),
    },
    {
      id: 'meeting',
      heading: '12. Cuando de verdad se reúnan',
      body: (
        <>
          <P>
            Esta es la parte de Tribys que ocurre donde no podemos verla, y la que
            más importa. Los{' '}
            <a href={legalPaths.termsEs}>Términos del Servicio</a> son claros en que
            no evaluamos a nadie y no somos parte de tus planes. Entonces:
          </P>
          <Ul
            items={[
              'Reúnanse en público, al menos la primera vez.',
              'Dile a alguien de confianza a dónde vas y con quién.',
              'Resuelve tu propio transporte, y guarda suficiente batería en el teléfono para poder irte.',
              'No le envíes dinero a nadie, ni antes ni después de conocerse.',
              'Cuida tu bebida y bebe a tu ritmo.',
              'Vete cuando quieras. No le debes tu noche a nadie.',
              'Trata a las personas en una reunión como estas Normas exigen en línea: el acoso en persona es una violación igual, y actuamos sobre ella.',
              'Reporta lo que pasó después, incluso si lo resolviste. Así es como encontramos a quienes reinciden.',
            ]}
          />
          <P>
            En una emergencia, contacta primero a los servicios de emergencia de tu
            localidad y después avísanos.
          </P>
        </>
      ),
    },
    {
      id: 'enforcement',
      heading: '13. Cómo hacemos cumplir esto',
      body: (
        <>
          <P>
            Parte de esto es automatizado y parte son personas. Preferimos decirte
            exactamente cuál es cuál que dejar que lo supongas.
          </P>
          <H3>Revisiones automatizadas, y qué cubren</H3>
          <Ul
            items={[
              <><strong>El texto que envías se analiza en busca de toxicidad.</strong> Esto cubre tu nombre y tu biografía, los títulos y descripciones de tribys, los nombres, descripciones, direcciones y ubicaciones de eventos de los Spots, y los nombres de intereses nuevos. Por encima de un umbral alto de confianza el envío se <strong>rechaza directamente</strong> y verás un error. En una banda intermedia, el triby o el Spot sí se crea pero <strong>aparece más abajo en las búsquedas y en el mapa</strong>.</>,
              <><strong>Las fotos de perfil se analizan</strong> en busca de desnudos y contenido explícito, para comprobar si realmente hay un rostro, y —si estás verificado— para comprobar si ese rostro coincide con tu imagen de referencia de verificación. Una foto puede rechazarse por cualquiera de esos motivos.</>,
              <><strong>Los mensajes privados no se analizan.</strong> No revisamos el contenido de tus conversaciones, ni de forma automatizada ni de otra manera. Eso significa que nada filtra un mensaje antes de que te llegue, y significa que <strong>reportar es la única forma en que nos enteramos de algo dicho en un chat</strong>. Por favor úsalo.</>,
            ]}
          />
          <P>
            Las revisiones automatizadas no son exactas ni completas. Se les escapan
            cosas, son más débiles en idiomas distintos del inglés, y a veces
            rechazan algo perfectamente correcto. Que una revisión se haya pasado, o
            que algo esté publicado, no significa que lo hayamos aprobado.
          </P>
          <H3>Personas</H3>
          <P>
            Los reportes llegan a nuestro equipo, que revisa la cuenta, el contenido
            y el historial alrededor, y decide. La revisión automatizada nunca
            suspende a nadie por sí sola: eso lo decide una persona.
          </P>
        </>
      ),
    },
    {
      id: 'reporting',
      heading: '14. Reportar y bloquear',
      body: (
        <>
          <P>
            <strong>Bloquear</strong> es inmediato y privado, y no necesitas un
            motivo. Un miembro bloqueado no puede contactarte ni verte, y no se le
            avisa. Úsalo sin reservas.
          </P>
          <P>
            <strong>Reportar</strong> es cómo nos enteramos. Puedes reportar a un
            miembro, un triby o un Spot desde la aplicación, y elegir un motivo:
            spam, acoso, discurso de odio, violencia, contenido sexual,
            autolesiones, bienes ilícitos, fraude, propiedad intelectual u otro.
            Agrega una descripción breve si puedes —qué pasó y dónde— porque es la
            diferencia entre un reporte sobre el que podemos actuar y uno sobre el
            que no.
          </P>
          <P>
            Los reportes son confidenciales. No le decimos a la persona quién la
            reportó. Revisamos todos los reportes, podemos pedirte más detalles y no
            siempre podremos informarte el resultado, pero bloquear a alguien
            siempre funciona, independientemente de lo que decidamos sobre un
            reporte.
          </P>
          <P>
            No presentes reportes que sabes falsos, ni uses el reporte para acosar a
            alguien. Eso es en sí mismo una violación.
          </P>
          <P>
            Para algo urgente, o algo que involucre a un menor o un riesgo inmediato
            para alguien, escribe también a <Tok value={entity.supportEmail} />{' '}
            además de reportarlo en la aplicación.
          </P>
        </>
      ),
    },
    {
      id: 'consequences',
      heading: '15. Qué pasa cuando alguien rompe estas reglas',
      body: (
        <>
          <P>
            Lo que hacemos depende de qué pasó, de si parece deliberado y de si ya
            había pasado antes. Nuestras opciones son:
          </P>
          <Ul
            items={[
              <><strong>Un envío rechazado.</strong> El texto o la foto no se acepta y puedes cambiarlo e intentar de nuevo.</>,
              <><strong>Menor visibilidad.</strong> Un triby o un Spot permanece pero aparece más abajo.</>,
              <><strong>Retiro de contenido.</strong> Retiramos una foto, un triby, un Spot o un mensaje.</>,
              <><strong>Una advertencia.</strong> Te decimos cuál es el problema y esperamos que se detenga.</>,
              <><strong>Pérdida de una función.</strong> Por ejemplo la insignia de verificación, o la posibilidad de crear tribys.</>,
              <><strong>Una suspensión temporal.</strong> Tu cuenta queda inutilizable por un periodo determinado y luego vuelve.</>,
              <><strong>Una expulsión permanente.</strong> Tu cuenta se cierra y no puedes crear otra.</>,
            ]}
          />
          <P>
            No siempre empezamos por arriba de esa lista.{' '}
            <strong>
              Cualquier cosa que involucre a un menor, una amenaza creíble,
              contenido sexual enviado a quien no lo pidió, o un fraude va
              directamente a expulsión permanente.
            </strong>{' '}
            Crear una cuenta nueva para evadir una expulsión es en sí mismo motivo
            para expulsar la nueva.
          </P>
          <P>
            Si te expulsamos, conservas el derecho de pedirnos tus datos y de que se
            eliminen: consulta el{' '}
            <a href={legalPaths.privacyEs}>Aviso de Privacidad</a>. Una expulsión no
            es un reembolso de lo que hayas pagado; la sección 11 de los{' '}
            <a href={legalPaths.termsEs}>Términos</a> cubre eso.
          </P>
        </>
      ),
    },
    {
      id: 'appeals',
      heading: '16. Si crees que nos equivocamos',
      body: (
        <>
          <P>
            A veces nos vamos a equivocar. Las revisiones automatizadas rechazan
            cosas que no deberían, y las personas malinterpretan situaciones.
          </P>
          <P>
            <strong>
              Escribe a <Tok value={entity.supportEmail} /> con
              &quot;Apelación&quot; en el asunto
            </strong>{' '}
            e indícanos la cuenta, qué se retiró o restringió, y por qué crees que
            la decisión fue equivocada. Todo lo que quieras que consideremos, ponlo
            en ese correo.
          </P>
          <P>
            <strong>Una persona lo revisará.</strong> Si una decisión sobre ti se
            tomó con apoyo de tratamiento automatizado —una foto rechazada, una
            descripción rechazada, un rostro que no coincidió— puedes pedir que una
            persona la revise, pedirnos que expliquemos las razones principales y
            darnos tu punto de vista. Registramos el resultado y, si nos
            equivocamos, lo revertimos y lo decimos.
          </P>
          <P>
            Residentes de Quebec y de Canadá: este es el derecho de revisión descrito
            en nuestro <a href={legalPaths.privacyEs}>Aviso de Privacidad</a>, y
            también puedes contactar a la autoridad de privacidad ahí señalada.
          </P>
        </>
      ),
    },
    {
      id: 'contact',
      heading: '17. Contacto',
      body: (
        <>
          <Ul
            items={[
              <>Reportes, apelaciones y cualquier urgencia: <Tok value={entity.supportEmail} /></>,
              <>Derechos de autor, marcas y reclamaciones sobre Spots: <Tok value={entity.copyrightEmail} /></>,
              <>Privacidad y solicitudes de datos: <Tok value={entity.privacyEmail} /></>,
            ]}
          />
          <P>
            <Tok value={entity.legalName} />
            <br />
            <Tok value={entity.address} />
          </P>
        </>
      ),
    },
  ],
};
